import { useState } from 'react';
import { Box, Button, Grid, Heading, Icon, Skeleton, Text, VStack } from '@chakra-ui/react';
import { MdAdd as AddIcon, MdGridView, MdSearchOff } from 'react-icons/md';
import { useLocation } from 'wouter';
import { useHomeSearchParams } from '@/hooks/useHomeSearchParams';
import { useScrollRestore } from '@/hooks/useScrollRestore';
import { homeSearchKey } from '@/utils/homeSearchUrl';
import { saveHomeScroll } from '@/utils/homeScroll';
import { useAllCollections } from '@/lib/collectionService';
import { useImportSheet } from '@/contexts/ImportSheetContext';
import { ROUTES } from '@/types';
import BrowseHeader from '@/components/BrowseHeader';
import { CollectionTile, AddCollectionTile } from '@/components/CollectionTile';
import BottomNavBar from '@/components/BottomNavBar';
import FloatingAIChefButton from '@/components/FloatingAIChefButton';
import ProfileSheet from '@/components/ProfileSheet';

export default function CollectionsPage() {
  const [, navigate] = useLocation();
  // Search lives in the URL so Back restores it (search → collection → back
  // keeps the results) and searches are shareable.
  const { searchTerm, debouncedSearch, setSearchTerm } = useHomeSearchParams();
  const [profileOpen, setProfileOpen] = useState(false);
  const { open: openImportSheet } = useImportSheet();

  const { collections: collectionsForDisplay, isLoading } = useAllCollections({
    search: debouncedSearch || undefined,
  });

  const scrollKey = homeSearchKey({ searchTerm: debouncedSearch, selectedTagsByGroup: {}, selectedRatings: [] });
  useScrollRestore(scrollKey, !isLoading && collectionsForDisplay.length > 0);

  const handleCollectionClick = (collectionId: string) => {
    saveHomeScroll(scrollKey);
    navigate(ROUTES.RECIPE_GROUP(collectionId));
  };

  return (
    <Box minH="100vh" bg="bg.canvas" pb={{ base: '80px', lg: 0 }}>
      <BrowseHeader
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        placeholder="Search collections…"
        showFilter={false}
        onProfileClick={() => setProfileOpen(true)}
      />

      <Box px={{ base: 4, lg: 8 }} pt={3}>
        {isLoading ? (
          <Grid templateColumns={{ base: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }} gap={3}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} borderRadius="12px" h="120px" />
            ))}
          </Grid>
        ) : collectionsForDisplay.length === 0 && !debouncedSearch.trim() ? (
          <VStack gap={4} py={12} textAlign="center">
            <Box
              w="52px"
              h="52px"
              borderRadius="14px"
              bg="bg.muted"
              display="flex"
              alignItems="center"
              justifyContent="center"
              mx="auto"
            >
              <Icon as={MdGridView} boxSize={6} color="text.muted" />
            </Box>
            <Heading fontFamily="heading" fontWeight="800" fontSize="xl" color="text.primary">
              No collections yet
            </Heading>
            <Text fontFamily="body" fontWeight="300" color="text.muted">
              Create your first collection to organise your recipes by themes or occasions
            </Text>
            <Button
              bg="accent.emphasis"
              color="white"
              _hover={{ bg: 'accent.emphasisHover' }}
              onClick={() => openImportSheet('collection')}
            >
              <AddIcon />
              Create Collection
            </Button>
          </VStack>
        ) : collectionsForDisplay.length === 0 ? (
          <VStack py={10} gap={2} textAlign="center">
            <Icon as={MdSearchOff} boxSize={6} color="text.muted" />
            <Text fontFamily="body" fontWeight="300" fontSize="13px" color="text.muted">
              No collections matched "{debouncedSearch}"
            </Text>
          </VStack>
        ) : (
          <Grid templateColumns={{ base: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }} gap={3}>
            {collectionsForDisplay.map((collection) => (
              <CollectionTile
                key={collection.id}
                collection={collection}
                onClick={() => handleCollectionClick(collection.id)}
                height="120px"
                showPreview
              />
            ))}
            <AddCollectionTile onClick={() => openImportSheet('collection')} height="120px" />
          </Grid>
        )}
      </Box>

      <ProfileSheet isOpen={profileOpen} onClose={() => setProfileOpen(false)} />
      <BottomNavBar />
      <FloatingAIChefButton />
    </Box>
  );
}
